import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search as SearchIcon } from 'lucide-react';
import { Media } from '../types';
import { searchMulti } from '../api/tmdb';
import MovieCard from '../components/MovieCard';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';

const Search: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState<Media[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);

  // Get search query from URL
  const query = searchParams.get('q') || '';

  const performSearch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await searchMulti(query.trim(), currentPage);
      setResults(response.results);
      setTotalPages(response.total_pages);
      setTotalResults(response.total_results);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to perform search. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [query, currentPage]);

  useEffect(() => {
    if (query.trim()) {
      performSearch();
    } else {
      setResults([]);
      setTotalPages(0);
      setTotalResults(0);
    }
  }, [query, currentPage, performSearch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {Array.from({ length: 10 }).map((_, index) => (
        <div key={index} className="animate-pulse">
          <div className="aspect-[2/3] bg-gray-300 dark:bg-dark-700 rounded-xl mb-2 shimmer"></div>
          <div className="h-3 bg-gray-300 dark:bg-dark-700 rounded w-2/3 shimmer"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Search Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Search Movies & Series
        </h1>
        <SearchBar onSearch={handleSearch} showFilters={false} />
      </motion.div>

      {/* Search Results */}
      {query && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {/* Results Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <SearchIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Search Results
              </h2>
            </div>
            {totalResults > 0 && (
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {totalResults.toLocaleString()} results
              </span>
            )}
          </div>

          {/* Loading State */}
          {loading && <LoadingSkeleton />}

          {/* Error State */}
          {error && (
            <div className="text-center py-8">
              <div className="text-red-500 text-base mb-3">{error}</div>
              <button
                onClick={performSearch}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm"
              >
                Try Again
              </button>
            </div>
          )}

          {/* No Results */}
          {!loading && !error && results.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="bg-dark-800/70 dark:bg-dark-700/70 rounded-xl p-6 flex flex-col items-center shadow-md max-w-xs w-full">
                <SearchIcon className="h-10 w-10 text-primary-500 mb-2" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                  No results found
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm text-center">
                  Try adjusting your search terms.
                </p>
              </div>
            </div>
          )}

          {/* Results Grid */}
          {!loading && !error && results.length > 0 && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {results.map((item) => (
                  <MovieCard key={`${item.media_type}-${item.id}`} item={item} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default Search; 